/**
 * Mock hooks for @threekit-tools/treble
 * These are used to replace Threekit dependencies
 * Data based on PlayCanvas Jeep Wrangler configurator specification
 *
 * When an attribute is changed via the UI, the mock `setAttribute` also
 * calls window.configurator.setConfig() through the playcanvasConfig bridge.
 */

import React from 'react';
import {
  ATTR_TO_PC_KEY,
  setPlayCanvasAttribute,
} from './playcanvasConfig';

// Hardcoded configuration data based on PlayCanvas config_data.md
const mockAttributes: any = {
  // ── Jeep Type Selector (2 Door vs 4 Door) ──
  'Jeep Type': {
    type: 'String',
    value: '4d',
    defaultValue: '4d',
    values: [
      { label: '2 Door', value: '2d', metadata: { option: '2d', asset: '2DJeep_Parts', shadowScale: 7 } },
      { label: '4 Door', value: '4d', metadata: { option: '4d', asset: '4DJeep_Parts', shadowScale: 8.6 } },
    ]
  },

  // ── Exterior Color (from PlayCanvas: Green, Red, Black, Orange, White, Grey) ──
  'Paint color': {
    type: 'Asset',
    value: { assetId: 'green-paint' },
    defaultValue: { assetId: 'green-paint' },
    values: [
      { label: 'Green', assetId: 'green-paint', value: 'green-paint', metadata: { _bg: '#2D5016', option: 'Green', _id: 'ext-color-0' } },
      { label: 'Red', assetId: 'red-paint', value: 'red-paint', metadata: { _bg: '#8B1A1A', option: 'Red', _id: 'ext-color-1' } },
      { label: 'Black', assetId: 'black-paint', value: 'black-paint', metadata: { _bg: '#000000', option: 'Black', _id: 'ext-color-2' } },
      { label: 'Yellow', assetId: 'orange-paint', value: 'orange-paint', metadata: { _bg: '#FFD700', option: 'Orange', _id: 'ext-color-3' } },
      { label: 'White', assetId: 'white-paint', value: 'white-paint', metadata: { _bg: '#FFFFFF', option: 'White', _id: 'ext-color-4' } },
      { label: 'Grey', assetId: 'grey-paint', value: 'grey-paint', metadata: { _bg: '#808080', option: 'Grey', _id: 'ext-color-5' } },
    ]
  },

  // ── Wheels/Rim (from PlayCanvas rimOption) ──
  'Wheels': {
    type: 'Asset',
    value: { assetId: 'default-rim' },
    defaultValue: { assetId: 'default-rim' },
    values: [
      { label: 'Default', assetId: 'default-rim', value: 'default', metadata: { _id: '14426', option: 'default', asset: 'Default_Rim_FL' } },
      { label: 'WHL Quad Hardroc', assetId: 'whl-quad-hardroc', value: 'whl-quad-hardroc', metadata: { _id: '14427', option: 'whl-quad-hardroc', asset: 'WHL_Quad_Hardroc_Rim_FL' } },
      { label: 'ZHE Quad CJ Retro AR', assetId: 'zhe-quad-cj-retro-ar', value: 'zhe-quad-cj-retro-ar', metadata: { _id: '22000', option: 'zhe-quad-cj-retro-ar', asset: 'ZHE_Quad_CJ_Retro_AR_Rim_FL' } },
    ]
  },

  // ── Tires (from PlayCanvas tireOption) ──
  'Tire': {
    type: 'Asset',
    value: { assetId: 'default-tire' },
    defaultValue: { assetId: 'default-tire' },
    values: [
      { label: 'Default', assetId: 'default-tire', value: 'default', metadata: { _id: '20469', option: 'default', asset: 'Default_Tire_FL' } },
      { label: 'Wrangler', assetId: 'wrangler-tire', value: 'wrangler', metadata: { _id: '20470', option: 'wrangler', asset: 'Wrangler_Tire_FL' } },
    ]
  },

  // ── Front Bumper (from PlayCanvas frontBumper) ──
  'Front Bumper': {
    type: 'Asset',
    value: { assetId: 'default-front-bumper' },
    defaultValue: { assetId: 'default-front-bumper' },
    values: [
      { label: 'Default', assetId: 'default-front-bumper', value: 'default', metadata: { _id: '13763', option: 'default', asset: 'Default_FrontBumper' } },
      { label: 'Carnivore', assetId: 'carnivore-bumper', value: 'carnivore', metadata: { _id: '15038', option: 'carnivore', asset: 'Carnivore_FrontBumper' } },
      { label: 'RAA Quad Alum', assetId: 'raa-quad-bumper', value: 'raa-quad', metadata: { _id: '23556', option: 'raa-quad', asset: 'FrontBumper_RAA_Quad_Alum' } },
    ]
  },

  // ── Rear Bumper (from PlayCanvas rearBumper) ──
  'Rear Bumper': {
    type: 'Asset',
    value: { assetId: 'default-rear-bumper' },
    defaultValue: { assetId: 'default-rear-bumper' },
    values: [
      { label: 'Default', assetId: 'default-rear-bumper', value: 'default', metadata: { _id: '13765', option: 'default', asset: 'Default_RearBumper' } },
      { label: 'RAA Quad Alum', assetId: 'raa-quad-rear', value: 'raa-quad', metadata: { _id: '21996', option: 'raa-quad', asset: 'RAA_Quad_Alum_RearBumper' } },
      { label: 'Rugged Ridge', assetId: 'rugged-ridge-rear', value: 'rugged-ridge', metadata: { _id: '26000', option: 'rugged-ridge', asset: 'Rugged_Ridge_RearBumper' } },
    ]
  },

  // ── Front Fender (from PlayCanvas frontFender) ──
  'Front Fender': {
    type: 'Asset',
    value: { assetId: 'default-fender' },
    defaultValue: { assetId: 'default-fender' },
    values: [
      { label: 'Default', assetId: 'default-fender', value: 'default', metadata: { _id: 'fender-def', option: 'default', asset: 'Defaut_Fender_FL' } },
      { label: 'Quadratec Baja HD', assetId: 'quadratec-baja-hd', value: 'quadratec-baja-hd', metadata: { _id: '26030', option: 'quadratec-baja-hd', asset: 'Quadratec_Baja_HD_Fender_FL' } },
      { label: 'Rugged Ridge Max', assetId: 'rugged-ridge-max', value: 'rugged-ridge-max', metadata: { _id: '25274', option: 'rugged-ridge-max', asset: 'Rugged_Ridge_Max_Fender_FL' } },
    ]
  },

  // ── Rear Fender (from PlayCanvas rearFender) ──
  'Rear Fender': {
    type: 'Asset',
    value: { assetId: 'default-rear-fender' },
    defaultValue: { assetId: 'default-rear-fender' },
    values: [
      { label: 'Default', assetId: 'default-rear-fender', value: 'default', metadata: { _id: 'rfender-def', option: 'default', asset: 'Defaut_Fender_RL' } },
      { label: 'Quadratec Baja HD', assetId: 'quadratec-baja-hd-rear', value: 'quadratec-baja-hd', metadata: { _id: '26031', option: 'quadratec-baja-hd', asset: 'Quadratec_Baja_HD_Fender_RL' } },
      { label: 'Rugged Ridge Max', assetId: 'rugged-ridge-max-rear', value: 'rugged-ridge-max', metadata: { _id: '25275', option: 'rugged-ridge-max', asset: 'Rugged_Ridge_Max_Fender_RL' } },
    ]
  },

  // ── Door (from PlayCanvas doorOption) ──
  'Door': {
    type: 'Asset',
    value: { assetId: 'default-door' },
    defaultValue: { assetId: 'default-door' },
    values: [
      { label: 'Default', assetId: 'default-door', value: 'default', metadata: { _id: 'door-def', option: 'default', asset: 'Default_Left_Front_Door' } },
    ]
  },

  // ── Mirror (from PlayCanvas mirrorOption) ──
  'Mirror': {
    type: 'Asset',
    value: { assetId: 'default-mirror' },
    defaultValue: { assetId: 'default-mirror' },
    values: [
      { label: 'Default', assetId: 'default-mirror', value: 'default', metadata: { _id: 'mirror-def', option: 'default', asset: 'Default_Mirror' } },
      { label: 'QR Mirror Rectangle', assetId: 'qr-rectangle', value: 'qr-rectangle', metadata: { _id: 'mirror-rect', option: 'qr-rectangle', asset: 'Quick_Release_Mirror_Rectangle' } },
      { label: 'QR Mirror Round', assetId: 'qr-round', value: 'qr-round', metadata: { _id: 'mirror-round', option: 'qr-round', asset: 'Quick_Release_Mirror_Round' } },
      { label: 'Quadratec Factory Styling', assetId: 'quadratec-factory', value: 'quadratec-factory', metadata: { _id: 'mirror-factory', option: 'quadratec-factory', asset: 'Quadratec_Factory_Styling_Mirror' } },
    ]
  },

  // ── Hood Catcher (from PlayCanvas hoodCatcherOption) ──
  'Hood Catcher': {
    type: 'Asset',
    value: { assetId: 'default-hoodcatcher' },
    defaultValue: { assetId: 'default-hoodcatcher' },
    values: [
      { label: 'Default', assetId: 'default-hoodcatcher', value: 'default', metadata: { _id: 'hc-def', option: 'default', asset: 'Default_HoodCatcher' } },
      { label: 'HGO Quad', assetId: 'hgo-quad', value: 'hgo-quad', metadata: { _id: 'hc-hgo', option: 'hgo-quad', asset: 'HGO_Quad_HoodCatcher' } },
      { label: 'Rugged Ridge', assetId: 'rugged-ridge-hc', value: 'rugged-ridge', metadata: { _id: 'hc-rr', option: 'rugged-ridge', asset: 'Rugged_Ridge_HoodCatcher' } },
    ]
  },

  // ── Light Bar (from PlayCanvas lightBarOption) ──
  'Light Bar': {
    type: 'Asset',
    value: { assetId: 'none-lightbar' },
    defaultValue: { assetId: 'none-lightbar' },
    values: [
      { label: 'None', assetId: 'none-lightbar', value: 'none', metadata: { _id: 'lb-none', option: 'none', asset: null } },
      { label: 'Light Bar', assetId: 'light-bar', value: 'light-bar', metadata: { _id: 'lb-bar', option: 'light-bar', asset: 'Light_Bar' } },
    ]
  },

  // ── Side Steps (from PlayCanvas sideStepOption) ──
  'Side Steps': {
    type: 'Asset',
    value: { assetId: 'none-sidestep' },
    defaultValue: { assetId: 'none-sidestep' },
    values: [
      { label: 'None', assetId: 'none-sidestep', value: 'none', metadata: { _id: 'ss-none', option: 'none', asset: null } },
      { label: 'Carnivore', assetId: 'carnivore-ss', value: 'carnivore', metadata: { _id: 'ss-carnivore', option: 'carnivore', asset: 'Carnivore_SideStep_4D' } },
      { label: 'Quadratec QR3', assetId: 'quadratec-qr3', value: 'quadratec-qr3', metadata: { _id: 'ss-qr3', option: 'quadratec-qr3', asset: 'Quadratec_QR3_SideStep_4D' } },
      { label: 'Quadratec QR4', assetId: 'quadratec-qr4', value: 'quadratec-qr4', metadata: { _id: 'ss-qr4', option: 'quadratec-qr4', asset: 'Quadratec_QR4_SideStep_4D' } },
    ]
  },
};

// Getter for mock attributes (used by priceHelpers.ts)
export function getMockAttributes(): Record<string, any> {
  return mockAttributes;
}

// Mock useAttribute hook
export const useAttribute = (attributeName: string) => {
  const attribute = mockAttributes[attributeName] || {
    type: 'String',
    value: '',
    defaultValue: '',
    values: []
  };

  const setAttribute = (newValue: any) => {
    console.log(`Setting ${attributeName} to:`, newValue);

    // ── 1. Update the local mock state ──
    if (attribute.type === 'Asset') {
      attribute.value = { assetId: newValue };
    } else {
      attribute.value = newValue;
    }

    // ── 2. Forward to PlayCanvas configurator ──
    // Resolve the PlayCanvas option value from the attribute values list.
    let pcOptionValue: any = newValue;
    let label: string | undefined;

    if (attribute.type === 'Asset') {
      // For assets: find the item by assetId and use its metadata.option / value
      const matched = attribute.values.find(
        (v: any) => v.assetId === newValue
      );
      if (matched) {
        pcOptionValue = matched.value ?? matched.metadata?.option ?? newValue;
        label = matched.label;
      }
    } else {
      // For string type: value is used directly
      const matched = attribute.values.find(
        (v: any) => v.value === newValue
      );
      if (matched) {
        label = matched.label;
      }
      pcOptionValue = newValue;
    }

    setPlayCanvasAttribute(attributeName, pcOptionValue, label);
  };

  return [attribute, setAttribute];
};

// Mock useConfigurator hook
export const useConfigurator = () => {
  const attributes = {
    'Jeep Type': mockAttributes['Jeep Type'].value,
    'Paint color': mockAttributes['Paint color'].value,
    'Bumper Width': mockAttributes['Front Fender']?.value,
    'Front Fender': mockAttributes['Front Fender']?.value,
    'Rear Fender': mockAttributes['Rear Fender']?.value,
    'Tire': mockAttributes['Tire'].value,
    'Wheels': mockAttributes['Wheels'].value,
    'Front Bumper': mockAttributes['Front Bumper'].value,
    'Rear Bumper': mockAttributes['Rear Bumper'].value,
    'Door': mockAttributes['Door']?.value,
    'Mirror': mockAttributes['Mirror']?.value,
    'Hood Catcher': mockAttributes['Hood Catcher']?.value,
    'Light Bar': mockAttributes['Light Bar']?.value,
    'Side Steps': mockAttributes['Side Steps']?.value,
  };

  const setConfiguration = (newConfig: any) => {
    console.log('Setting configuration:', newConfig);
    Object.assign(attributes, newConfig);

    // Forward bulk config to PlayCanvas
    const configurator = (window as any).configurator;
    if (configurator?.setConfig) {
      const pcPatch: Record<string, any> = {};
      for (const [uiKey, uiVal] of Object.entries(newConfig)) {
        const pcKey = ATTR_TO_PC_KEY[uiKey];
        if (pcKey) {
          pcPatch[pcKey] = uiVal;
        }
      }
      if (Object.keys(pcPatch).length > 0) {
        configurator.setConfig(pcPatch);
      }
    }
  };

  return [attributes, setConfiguration];
};

// Mock useThreekitInitStatus hook
export const useThreekitInitStatus = () => {
  return true;
};

// Mock useProductCache hook
export const useProductCache = () => {
  const cache = {};
  const products = {};
  
  const handleLoadProduct = (productId: string) => {
    console.log('Loading product:', productId);
    return Promise.resolve();
  };

  return [{ cache, products }, handleLoadProduct];
};

// Mock usePlayerPortal hook
export const usePlayerPortal = () => {
  return null;
};

// Mock Share component
export const Share = () => null;

// Mock Player component
export const Player = () => null;

// Mock Snapshots component
export const Snapshots = () => null;

// Mock Wishlist component
export const Wishlist = () => null;

// Mock Zoom component
export const Zoom = () => null;

// Mock ProductLayout component
export const ProductLayout = ({ children }: any) => children;

// Mock CaretDownIcon component
export const CaretDownIcon = () => React.createElement('svg', {
  width: '12',
  height: '7',
  viewBox: '0 0 12 7',
  fill: 'currentColor'
}, React.createElement('path', {
  d: 'M1 1l5 5 5-5'
}));
